import * as PropertiesSelector from "../../../../../../properties-selector";
import * as SharedState from "../../../../../../shared-state";
import * as LanguageTexts from "@genesys/shared/lib/language-texts";
import { getWarningTextKey } from "../../../utils";
import {
  PropertyDefinitions,
  PropertyDefinition,
  definitionIsValid,
  propertyValueAllowed
} from "../../../../../tools";
import { PropertyValueSet, PropertyFilter } from "@genesys/property";
import { LoadDef, WarningType } from "./types";

export function getWarningsAndErrors(
  load: LoadDef,
  isLoadNameEmpty: boolean,
  isLoadNameDuplicated: boolean,
  propertiesSelectorState: PropertiesSelector.State,
  allPropertyDefinitions: PropertyDefinitions,
  sharedState: SharedState.State
) {
  const currentSelections = PropertiesSelector.getSelectedProperties(
    propertiesSelectorState
  )!;

  const hasError = !load.properties.loadProperties
    .map(pn => allPropertyDefinitions.find(pd => pd.name === pn)!)
    .every(definition => definitionIsValid(definition, currentSelections));

  const messages = [];

  const warningTextKey = getWarningTextKey(load.sourceName, currentSelections);

  const warningMessage =
    warningTextKey &&
    sharedState.translate(LanguageTexts.globalPropertyName(warningTextKey));

  let warningType: WarningType = "none";
  if (warningMessage) {
    messages.push(warningMessage);
    warningType = "warning";
  }

  if (hasError) {
    warningType = "error";

    messages.unshift(
      ...getErrorMessages(
        load.properties.loadProperties,
        allPropertyDefinitions,
        currentSelections,
        load,
        sharedState.translate
      )
    );
  }

  if (isLoadNameEmpty) {
    warningType = "error";
    messages.unshift(
      sharedState.translate(LanguageTexts.mlcEmptyLoadTypeName())
    );
  }

  if (isLoadNameDuplicated) {
    warningType = "error";
    messages.unshift(
      sharedState.translate(LanguageTexts.mlcDuplicatedLoadTypeName())
    );
  }

  return { messages, warningType };
}

function getErrorMessages(
  properties: ReadonlyArray<string>,
  allPropertyDefinitions: PropertyDefinitions,
  selectedProperties: PropertyValueSet.PropertyValueSet,
  load: LoadDef,
  translate: (textDefinition: LanguageTexts.TextDefinition) => string
) {
  const invalidProperties: Array<string> = [];

  for (const property of properties) {
    const propertyDef = allPropertyDefinitions.find(
      pd => pd.name === property
    )!;
    if (definitionIsValid(propertyDef, selectedProperties)) {
      continue;
    }
    getErrorReason(
      selectedProperties,
      propertyDef,
      allPropertyDefinitions,
      invalidProperties,
      load,
      translate
    );
  }
  return invalidProperties;
}

function getErrorReason(
  currentSelections: PropertyValueSet.PropertyValueSet,
  definition: PropertyDefinition,
  allPropertyDefinitions: PropertyDefinitions,
  invalidProperties: string[],
  load: LoadDef,
  translate: (textDefinition: LanguageTexts.TextDefinition) => string
) {
  const propertyNameTranslated = translate(
    LanguageTexts.globalPropertyName(definition.name)
  );
  if (!PropertyFilter.isValid(currentSelections, definition.validationFilter)) {
    if (definition.name.startsWith("mlcuse")) {
      let errorMessage = builMlcUseError(
        definition,
        allPropertyDefinitions,
        currentSelections,
        load
      );

      errorMessage += ` PropertyFIlter: (${PropertyFilter.toString(
        definition.validationFilter
      )})`;

      invalidProperties.push(errorMessage);

      return;
    }
    invalidProperties.push(`property ${propertyNameTranslated} invalid`);
  }

  // Check discrete.
  invalidProperties.push(
    `property value ${translate(
      LanguageTexts.globalPropertyName(
        definition.name +
          "_" +
          PropertyValueSet.getInteger(definition.name, currentSelections)
      )
    )} invalid for ${propertyNameTranslated} `
  );
}

function builMlcUseError(
  definition: PropertyDefinition,
  allPropertyDefinitions: PropertyDefinitions,
  currentSelections: PropertyValueSet.PropertyValueSet,
  load: LoadDef
): string {
  let message = "";
  const moistureAdded = load.additionalInfo.isMoistureAdded(currentSelections);
  const heatAdded = load.additionalInfo.isHeatAdded(currentSelections);

  // check if booth moistture and heat are added to load
  if (moistureAdded && heatAdded) {
    message +=
      "Both Moisture and heat loads cannot be used on this load for the current selections.";
    if (
      load.properties.heatProperty &&
      definitionIsValid(
        definition,
        PropertyValueSet.setInteger(
          load.properties.heatProperty,
          0,
          currentSelections
        )
      ) &&
      propertyValueAllowed(
        currentSelections,
        load.properties.heatProperty,
        0,
        allPropertyDefinitions
      )
    ) {
      message += " Try removing heat.";
    } else if (
      load.properties.moistureProperty &&
      definitionIsValid(
        definition,
        PropertyValueSet.setInteger(
          load.properties.moistureProperty,
          0,
          currentSelections
        )
      ) &&
      propertyValueAllowed(
        currentSelections,
        load.properties.moistureProperty,
        0,
        allPropertyDefinitions
      )
    ) {
      message += " Try removing moisture.";
    } else {
      message = "This load cannot be active";
    }
    // Check if only moisture is added
  } else if (moistureAdded) {
    message = `Moisture cannot be used on this load for the current selections.`;
    if (
      load.properties.heatProperty &&
      definitionIsValid(
        definition,
        PropertyValueSet.setInteger(
          load.properties.heatProperty,
          1,
          currentSelections
        )
      ) &&
      propertyValueAllowed(
        currentSelections,
        load.properties.heatProperty,
        1,
        allPropertyDefinitions
      )
    ) {
      message += " Try adding heat aswell.";
      return message;
    }
    const moistureDeselected = PropertyValueSet.setInteger(
      load.properties.moistureProperty!,
      0,
      currentSelections
    );
    if (
      load.properties.heatProperty &&
      definitionIsValid(
        definition,
        PropertyValueSet.setInteger(
          load.properties.heatProperty,
          1,
          moistureDeselected
        )
      ) &&
      propertyValueAllowed(
        moistureDeselected,
        load.properties.heatProperty,
        1,
        allPropertyDefinitions
      )
    ) {
      message += " Try using only heat instead.";
    } else {
      message = "This load cannot be active.";
    }
    // Check if only heat is added
  } else if (heatAdded) {
    message = `Heat cannot be added on this load for the current selections.`;

    if (
      load.properties.moistureProperty &&
      definitionIsValid(
        definition,
        PropertyValueSet.setInteger(
          load.properties.moistureProperty,
          1,
          currentSelections
        )
      ) &&
      propertyValueAllowed(
        currentSelections,
        load.properties.moistureProperty,
        1,
        allPropertyDefinitions
      )
    ) {
      message += " Try adding moisture aswell.";
      return message;
    }

    const heatDeselected = PropertyValueSet.setInteger(
      load.properties.heatProperty!,
      0,
      currentSelections
    );

    if (
      load.properties.moistureProperty &&
      definitionIsValid(
        definition,
        PropertyValueSet.setInteger(
          load.properties.moistureProperty,
          1,
          heatDeselected
        )
      ) &&
      propertyValueAllowed(
        heatDeselected,
        load.properties.moistureProperty,
        1,
        allPropertyDefinitions
      )
    ) {
      message += " Try using only moisture.";
    } else {
      message = "This load cannot be active.";
    }
  }

  return message;
}
